import { Component, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { ITasksSummary } from '../../../../modules/tasks/model/types';
import { LegendItem, LegendMarkerState } from 'devextreme/common/charts';
import { DxChartTypes } from 'devextreme-angular/ui/chart';
import notify from 'devextreme/ui/notify';
import { startOfDay, endOfDay } from 'date-fns';
import { Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DashboardDataService } from '../services/dashboard-data.service';
import { map, Observable, Subscription, skip } from 'rxjs';
import { DatePanelItems, datePanelItems } from '../../../../types/resource';
import {
  IDashboardArgs,
  IDashboardFilterOptions,
  ITaskNavigationFilterArgs,
} from '../types/dashboard-types';
import { IDashboardFilterChangeEvent } from '../types/dashboard-types';
import { FilterStateService } from '../../../services/filter-state.service';
import { SignalRService } from '../../../services/signal-r.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: false,
  selector: 'app-dashboard-container',
  templateUrl: './dashboard-container.component.html',
  styleUrls: ['./dashboard-container.component.css'],
})
export class DashboardContainerComponent implements OnInit, OnDestroy {
  startDate: Date;
  endDate: Date;
  tasksSummaryDataSource: ITasksSummary[];
  teamsSummaryDataSource: any[];
  typesSummaryDataSource: any[];
  peopleSummaryDataSource: any[];
  countByPeriodDataSource: any;
  isLoading: boolean = true;
  countByDateTitle: string;
  filterOptions: DatePanelItems[] = [];
  showCompleted: boolean = false;
  showRejected: boolean = false;
  storageKey: string = 'dashboard_container';
  private filterStateSubscription: Subscription;

  // SignalR throttling properties - now configurable from environment
  private readonly dashboardConfig = {
    normalThrottleMs: 30000,      // Normal throttle: 30 seconds
    highActivityThrottleMs: 5000, // High activity throttle: 5 seconds
    highActivityThreshold: 5,     // Threshold for high activity (5+ updates)
    activityWindowMs: 30000       // Time window to count activity (30 seconds)
  };
  private lastDashboardUpdate: number = 0;
  private pendingDashboardUpdate: boolean = false;
  private updateActivity: number[] = []; // Track timestamps of recent updates

  loadFilterOptions(): void {
    const filterState = this.filterStateService.loadFilterState();
    this.showCompleted = filterState.showCompleted;
    this.showRejected = filterState.showRejected;
    this.filterOptions = filterState.filterOptions;
  }

  private setDefaultFilterOptions() {
    this.showCompleted = false; // Default value for showCompleted
    this.showRejected = false; // Default value for showRejected
    this.filterOptions = datePanelItems.map((item) => ({
      ...item,
      selectedItem: item.items[0].text, // Default selectedValue to the first value in the 'values' array
    }));
    // Save the default options to the shared service
    const filterState = {
      showCompleted: this.showCompleted,
      showRejected: this.showRejected,
      filterOptions: this.filterOptions,
    };
    this.filterStateService.saveFilterState(filterState);
  }

  onTableValueClicked(event: ITaskNavigationFilterArgs): void {
    const navState = {...event, filterTypeMessage: 'Filtered by dashboard selection'}
    this.router.navigate(['/tasks'], { state: navState });

    //    this.router.navigate(['/tasks'],{ state: {startDate: this.startDate, endDate: this.endDate, name: e.target.data.name,
    //      taskStatus: e.target.data.status, argument: e.target.originalArgument}});
  }

  getDashboardArgsFromFilterOptions(): IDashboardArgs {
    const result: IDashboardArgs = {
      startDate: null,
      endDate: null,
      scope: null,
      dueOption: null,
      showOpen: !this.showCompleted,
      showRejected: this.showRejected,
    };

    // Find the object with name: 'Due'
    const dueOption = this.filterOptions.find((panel) => panel.name === 'Due');
    if (dueOption && dueOption.selectedItem) {
      const selectedDueItem = dueOption.items.find(
        (item) => item.text === dueOption.selectedItem
      );
      if (selectedDueItem) {
        result.dueOption = selectedDueItem.text;
        result.startDate = selectedDueItem.startDate;
        result.endDate = selectedDueItem.endDate;
      }
    }

    // Find the object with name: 'Scope'
    const scopeOption = this.filterOptions.find(
      (panel) => panel.name === 'Scope'
    );
    if (scopeOption && scopeOption.selectedItem) {
      const selectedScopeItem = scopeOption.items.find(
        (item) => item.text === scopeOption.selectedItem
      );
      if (selectedScopeItem) {
        result.scope = selectedScopeItem.text;
      }
    }

    return result;
  }

  cols: number = 2;

  constructor(
    private readonly router: Router,
    private readonly dashboardDataService: DashboardDataService,
    private breakpointObserver: BreakpointObserver,
    private filterStateService: FilterStateService,
    private readonly signalRService: SignalRService,
    private readonly destroyRef: DestroyRef
  ) {
    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.Handset])
      .subscribe((result) => {
        this.cols = result.matches ? 1 : 2; // 1 column for small screens, 2 for larger screens
      });

    this.loadFilterOptions();
    this.initialiseDates();
    // Removed the duplicate call - initialization will happen in ngOnInit

    this.dashboardData$ = this.dashboardDataService.dashboardTables$;
  }

  dashboardData$: Observable<any[]>;

  initialiseDates() {
    let dt = new Date();
    this.startDate = startOfDay(dt);
    this.endDate = endOfDay(dt);
  }

  ngOnInit(): void {
    // Initial dashboard data load (using current filter state)
    // console.log('Dashboard Container: Initial dashboard data load');
    this.dashboardDataService.selectedDashboardArgsChanged(
      this.getDashboardArgsFromFilterOptions()
    );

    // Set up SignalR listeners for task changes
    this.setUpSignalRListeners();

    // Subscribe to filter state changes, skipping the initial emission to avoid duplicate calls
    this.filterStateSubscription = this.filterStateService.filterState$
      .pipe(skip(1)) // Skip the initial BehaviorSubject emission
      .subscribe((filterState) => {
        // console.log('Dashboard Container: filterState$ subscription triggered', filterState);
        // Update local properties from filter state
        this.showCompleted = filterState.showCompleted;
        this.showRejected = filterState.showRejected;
        this.filterOptions = filterState.filterOptions;

        // Trigger dashboard data update only on actual filter changes
        // console.log('Dashboard Container: calling selectedDashboardArgsChanged from subscription');
        this.dashboardDataService.selectedDashboardArgsChanged(
          this.getDashboardArgsFromFilterOptions()
        );
      });
  }

  ngOnDestroy(): void {
    if (this.filterStateSubscription) {
      this.filterStateSubscription.unsubscribe();
    }
  }

  private setUpSignalRListeners(): void {
    // Listen for task creation events
    this.signalRService.taskCreated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      // console.log('🔔 Dashboard Container: SignalR Task Created Received', data);
      this.requestThrottledDashboardUpdate('TaskCreated');
    });

    // Listen for task update events
    this.signalRService.taskChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      // console.log('🔔 Dashboard Container: SignalR Task Changed Received', data);
      this.requestThrottledDashboardUpdate('TaskChanged');
    });

    // Listen for task deletion events
    this.signalRService.taskDeleted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      // console.log('🔔 Dashboard Container: SignalR Task Deleted Received', data);
      this.requestThrottledDashboardUpdate('TaskDeleted');
    });
  }

  private requestThrottledDashboardUpdate(eventType: string): void {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastDashboardUpdate;

    // Add this update to activity tracking
    this.updateActivity.push(now);

    // Clean up old activity entries (outside the activity window)
    const activityWindowStart = now - this.dashboardConfig.activityWindowMs;
    this.updateActivity = this.updateActivity.filter(timestamp => timestamp > activityWindowStart);

    // Determine if we're in high activity mode
    const isHighActivity = this.updateActivity.length >= this.dashboardConfig.highActivityThreshold;
    const currentThrottleMs = isHighActivity
      ? this.dashboardConfig.highActivityThrottleMs
      : this.dashboardConfig.normalThrottleMs;

    // console.log(`📊 Dashboard Update Request (${eventType}):`, {
    //   timeSinceLastUpdate,
    //   normalThrottle: this.dashboardConfig.normalThrottleMs,
    //   highActivityThrottle: this.dashboardConfig.highActivityThrottleMs,
    //   currentThrottle: currentThrottleMs,
    //   recentUpdates: this.updateActivity.length,
    //   highActivityThreshold: this.dashboardConfig.highActivityThreshold,
    //   isHighActivity,
    //   canUpdateNow: timeSinceLastUpdate >= currentThrottleMs,
    //   pendingUpdate: this.pendingDashboardUpdate
    // });

    // If enough time has passed since the last update, update immediately
    if (timeSinceLastUpdate >= currentThrottleMs) {
      this.performDashboardUpdate(eventType, isHighActivity);
    } else {
      // Schedule an update for later if one isn't already pending
      if (!this.pendingDashboardUpdate) {
        this.pendingDashboardUpdate = true;
        const remainingTime = currentThrottleMs - timeSinceLastUpdate;

        // console.log(`⏳ Scheduling dashboard update in ${Math.round(remainingTime / 1000)}s ${isHighActivity ? '(high activity mode)' : '(normal mode)'}`);

        setTimeout(() => {
          this.performDashboardUpdate(`${eventType}-throttled`, isHighActivity);
          this.pendingDashboardUpdate = false;
        }, remainingTime);
      }
    }
  }

  private performDashboardUpdate(reason: string, isHighActivity: boolean = false): void {
    // console.log(`🔄 Performing dashboard update (${reason})${isHighActivity ? ' [HIGH ACTIVITY MODE]' : ' [NORMAL MODE]'}`);
    this.lastDashboardUpdate = Date.now();

    this.dashboardDataService.selectedDashboardArgsChanged(
      this.getDashboardArgsFromFilterOptions()
    );
  }

  // Helper method to get current throttling configuration (useful for debugging)
  public getDashboardThrottleConfig() {
    const now = Date.now();
    const activityWindowStart = now - this.dashboardConfig.activityWindowMs;
    const recentUpdates = this.updateActivity.filter(timestamp => timestamp > activityWindowStart).length;
    const isHighActivity = recentUpdates >= this.dashboardConfig.highActivityThreshold;

    return {
      normalThrottleMs: this.dashboardConfig.normalThrottleMs,
      highActivityThrottleMs: this.dashboardConfig.highActivityThrottleMs,
      highActivityThreshold: this.dashboardConfig.highActivityThreshold,
      activityWindowMs: this.dashboardConfig.activityWindowMs,
      currentRecentUpdates: recentUpdates,
      isHighActivityMode: isHighActivity,
      currentThrottleMs: isHighActivity ? this.dashboardConfig.highActivityThrottleMs : this.dashboardConfig.normalThrottleMs,
      lastUpdateMs: this.lastDashboardUpdate,
      timeSinceLastUpdate: now - this.lastDashboardUpdate
    };
  }
}
